import type { FunctionalComponent as FC } from 'preact';
import { useEffect } from 'preact/hooks';

import { clsx } from 'clsx';

import { useVkCaptchaContext } from '../../context/VkCaptchaContext';
import { useWidget } from '../../context/WidgetContext';
import { useAdaptiveWidget } from '../../hooks/useAdaptiveWidget';
import { useWidgetHistory } from '../../hooks/useWidgetHistory';
import { tokenManager } from '../../utils';

import { Captcha } from '../Captcha';
import { ErrorNotify } from '../ErrorNotify';
import { LoadHistoryButton } from '../LoadHistoryButton';
import { PhoneInputOverlay } from '../PhoneInputOverlay';
import { WidgetContent } from '../WidgetContent';
import { WidgetEditorArea } from '../WidgetEditorArea';
import { WidgetHeader } from '../WidgetHeader';

import styles from './styles.module.css';

interface Props {
  isOpen: boolean;
  onClose?: () => void;
}

export const WidgetPanel: FC<Props> = ({ isOpen, onClose }) => {
  const { isMobile } = useAdaptiveWidget();

  const { isCaptchaShown, handleCaptchaOpen, isCaptchaPassed } = useVkCaptchaContext();
  const { messages, companyLogoSrc, widgetTitle, error, hasMaxUserPhone } = useWidget();
  const { handleGetHistory, hasNextPage, isLoading } = useWidgetHistory();

  // Запрашиваем историю при открытии виджета
  useEffect(() => {
    if (isOpen) {
      const token = tokenManager.getToken();
      const isTokenExpired = tokenManager.getIsTokenExpired();

      // Если есть токен и он живой, запросим историю
      if (token && !isTokenExpired) {
        handleGetHistory();
      }
    }
    // handleGetHistory намеренно не в зависимостях: его ссылка меняется при каждой загрузке, иначе получим цикл запросов
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isOpen, isCaptchaPassed, hasMaxUserPhone]);

  const handleCloseWidget = () => {
    onClose?.();
  };

  const widgetPanelClass = clsx(styles.widgetPanel, {
    [styles.isMobile]: isMobile,
    [styles.isWidgetOpen]: isOpen,
  });

  return (
    <div className={widgetPanelClass}>
      <div className={styles.widgetPanelContent}>
        <ErrorNotify error={error} />

        <LoadHistoryButton onClick={handleCaptchaOpen} />

        <WidgetHeader logoSrc={companyLogoSrc} title={widgetTitle} onClose={handleCloseWidget} />

        <WidgetContent
          messages={messages}
          handleGetHistory={handleGetHistory}
          hasNextPage={hasNextPage}
          isLoading={isLoading}
        />

        <WidgetEditorArea />

        {isCaptchaShown ? <Captcha /> : null}

        <PhoneInputOverlay />
      </div>
    </div>
  );
};
